/**
 * Menu Icon Component
 *
 * Renders menu icons from Bootstrap Icons or custom SVG.
 * Supports fallback icon for unknown icon names.
 *
 * @since 2025-12-16
 * @epic INIT-0045, INIT-0046
 */

import clsx from 'clsx';

interface MenuIconProps {
  icon?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Default icons for common menu items.
 */
const DEFAULT_ICONS: Record<string, string> = {
  dashboard: 'bi-house-door',
  home: 'bi-house-door',
  person: 'bi-people',
  pessoas: 'bi-people',
  people: 'bi-people',
  product: 'bi-box',
  produtos: 'bi-box',
  box: 'bi-box',
  resource: 'bi-archive',
  recursos: 'bi-archive',
  archive: 'bi-archive',
  campaign: 'bi-megaphone',
  campanhas: 'bi-megaphone',
  content: 'bi-file-text',
  conteudo: 'bi-file-text',
  store: 'bi-shop',
  loja: 'bi-shop',
  merchandising: 'bi-bullseye',
  'shop-window': 'bi-bullseye',
  analytics: 'bi-bar-chart',
  settings: 'bi-gear',
  config: 'bi-gear',
  configuracoes: 'bi-gear',
  user: 'bi-person',
  profile: 'bi-person-circle',
  logout: 'bi-box-arrow-right',
  sair: 'bi-box-arrow-right',
  help: 'bi-question-circle',
  ajuda: 'bi-question-circle',
  search: 'bi-search',
  buscar: 'bi-search',
  menu: 'bi-list',
  list: 'bi-list-ul',
  lista: 'bi-list-ul',
  folder: 'bi-folder',
  file: 'bi-file-earmark',
  'file-text': 'bi-file-text',
  calendar: 'bi-calendar',
  agenda: 'bi-calendar',
  chart: 'bi-graph-up',
  report: 'bi-file-earmark-bar-graph',
  relatorio: 'bi-file-earmark-bar-graph',
  'status-vendas': 'bi-graph-up-arrow',
  'status_vendas': 'bi-graph-up-arrow',
  'statusvendas': 'bi-graph-up-arrow',
  'sales-status': 'bi-graph-up-arrow',
  map: 'bi-geo-alt',
  mapa: 'bi-geo-alt',
  notification: 'bi-bell',
  notificacao: 'bi-bell',
  message: 'bi-chat',
  mensagem: 'bi-chat',
  comunicacao: 'bi-megaphone',
  communication: 'bi-megaphone',
  chat: 'bi-chat-dots-fill',
  'chat-dots': 'bi-chat-dots',
  headset: 'bi-headset',
  sac: 'bi-headset',
  'ticket-detailed': 'bi-ticket-detailed',
  ticket: 'bi-ticket-detailed',
  kanban: 'bi-kanban',
  'diagram-3': 'bi-diagram-3',
  'clock-history': 'bi-clock-history',
  email: 'bi-envelope',
  phone: 'bi-telephone',
  telefone: 'bi-telephone',
  link: 'bi-link-45deg',
  external: 'bi-box-arrow-up-right',
  download: 'bi-download',
  upload: 'bi-upload',
  trash: 'bi-trash',
  lixeira: 'bi-trash',
  edit: 'bi-pencil',
  editar: 'bi-pencil',
  view: 'bi-eye',
  visualizar: 'bi-eye',
  add: 'bi-plus-lg',
  adicionar: 'bi-plus-lg',
  plus: 'bi-plus-lg',
  novo: 'bi-plus-lg',
  remove: 'bi-dash-lg',
  remover: 'bi-dash-lg',
  heart: 'bi-heart',
  coracao: 'bi-heart',
  'heart-fill': 'bi-heart-fill',
  star: 'bi-star',
  estrela: 'bi-star',
  'star-fill': 'bi-star-fill',
  flag: 'bi-flag',
  bandeira: 'bi-flag',
  'flag-fill': 'bi-flag-fill',
  shield: 'bi-shield',
  escudo: 'bi-shield',
  lightning: 'bi-lightning',
  raio: 'bi-lightning',
  fire: 'bi-fire',
  fogo: 'bi-fire',
  check: 'bi-check-lg',
  close: 'bi-x-lg',
  fechar: 'bi-x-lg',
  arrow_right: 'bi-chevron-right',
  arrow_left: 'bi-chevron-left',
  arrow_down: 'bi-chevron-down',
  arrow_up: 'bi-chevron-up',
  tag: 'bi-tag',
  tags: 'bi-tags',
  layers: 'bi-layers',
  grid: 'bi-grid',
  package: 'bi-box-seam',
};

/**
 * Size classes for icons.
 */
const SIZE_CLASSES = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};

/**
 * Get Bootstrap Icon class from icon string.
 */
function getIconClass(icon?: string): string {
  if (!icon) {
    return 'bi-circle'; // Fallback icon
  }

  // Already a Bootstrap Icon class
  if (icon.startsWith('bi-')) {
    return icon;
  }

  // FontAwesome fallback (legacy)
  if (icon.startsWith('fa-') || icon.startsWith('fas ') || icon.startsWith('far ')) {
    // Map common FA icons to BI
    const faName = icon.replace(/^(fa-|fas |far )/, '').toLowerCase();
    return DEFAULT_ICONS[faName] || 'bi-circle';
  }

  // Glyphicon fallback (legacy)
  if (icon.startsWith('glyphicon-')) {
    const glyphName = icon.replace('glyphicon-', '').toLowerCase();
    return DEFAULT_ICONS[glyphName] || 'bi-circle';
  }

  // Check default icons by name
  const normalizedIcon = icon.toLowerCase().replace(/[-_\s]/g, '');
  for (const [key, value] of Object.entries(DEFAULT_ICONS)) {
    if (normalizedIcon.includes(key.toLowerCase())) {
      return value;
    }
  }

  // Try direct mapping
  if (DEFAULT_ICONS[icon.toLowerCase()]) {
    return DEFAULT_ICONS[icon.toLowerCase()];
  }

  return 'bi-circle'; // Fallback
}

/**
 * Menu Icon component.
 *
 * Renders Bootstrap Icons with fallback support.
 */
export function MenuIcon({ icon, className, size = 'md' }: MenuIconProps) {
  const iconClass = getIconClass(icon);

  return (
    <i
      className={clsx(
        'bi',
        iconClass,
        SIZE_CLASSES[size],
        className
      )}
      aria-hidden="true"
    />
  );
}

/**
 * SVG-based icons for when Bootstrap Icons aren't loaded.
 */
export function MenuIconSvg({ icon, className, size = 'md' }: MenuIconProps) {
  const sizeMap = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  // Dashboard/Home icon
  if (!icon || icon.includes('home') || icon.includes('dashboard')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    );
  }

  // People icon
  if (icon.includes('people') || icon.includes('person') || icon.includes('user')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    );
  }

  // Box/Product icon
  if (icon.includes('box') || icon.includes('product') || icon.includes('produto')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    );
  }

  // Archive/Resource icon
  if (icon.includes('archive') || icon.includes('resource') || icon.includes('recurso')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    );
  }

  // Campaign icon (target/bullseye)
  if (icon.includes('campaign') || icon.includes('campanha')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="10" strokeWidth={2} />
        <circle cx="12" cy="12" r="6" strokeWidth={2} />
        <circle cx="12" cy="12" r="2" strokeWidth={2} />
      </svg>
    );
  }

  // SAC/Headset icon
  if (icon.includes('headset') || icon.includes('sac')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M8 1a5 5 0 0 0-5 5v1h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a6 6 0 1 1 12 0v6a2.5 2.5 0 0 1-2.5 2.5H9.366a1 1 0 0 1-.866.5h-1a1 1 0 1 1 0-2h1a1 1 0 0 1 .866.5H11.5A1.5 1.5 0 0 0 13 12h-1a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1V6a5 5 0 0 0-5-5z"/>
      </svg>
    );
  }

  // Kanban icon
  if (icon.includes('kanban')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M13.5 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zm-11-1a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z"/>
        <path d="M6.5 3a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1zm-4 0a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1zm8 0a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1z"/>
      </svg>
    );
  }

  // Ticket icon
  if (icon.includes('ticket')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M1.5 3A1.5 1.5 0 0 0 0 4.5V6a.5.5 0 0 0 .5.5 1.5 1.5 0 1 1 0 3 .5.5 0 0 0-.5.5v1.5A1.5 1.5 0 0 0 1.5 13h13a1.5 1.5 0 0 0 1.5-1.5V10a.5.5 0 0 0-.5-.5 1.5 1.5 0 0 1 0-3A.5.5 0 0 0 16 6V4.5A1.5 1.5 0 0 0 14.5 3z"/>
      </svg>
    );
  }

  // Communication/Chat icon
  if (icon.includes('comunicacao') || icon.includes('communication') || icon.includes('chat-dots')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="currentColor"
        viewBox="0 0 16 16"
      >
        <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"/>
        <path d="M2.165 15.803l.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.272.362a21.673 21.673 0 0 0 .693-.125z"/>
      </svg>
    );
  }

  // Chart icon
  if (icon.includes('chart') || icon.includes('graph')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"
        />
      </svg>
    );
  }

  // Gear/Settings icon
  if (icon.includes('gear') || icon.includes('setting') || icon.includes('config')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    );
  }

  // Folder icon
  if (icon.includes('folder')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
        />
      </svg>
    );
  }

  // List icon
  if (icon.includes('list') || icon.includes('lista')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 10h16M4 14h16M4 18h16"
        />
      </svg>
    );
  }

  // Plus/Add icon
  if (icon.includes('plus') || icon.includes('add') || icon.includes('novo')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
    );
  }

  // File/Text icon
  if (icon.includes('file') || icon.includes('text') || icon.includes('content') || icon.includes('conteudo')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }

  // Award/Star icon (Avaliação)
  if (icon.includes('award') || icon.includes('star') || icon.includes('avaliacao')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
        />
      </svg>
    );
  }

  // Telephone/Contact icon (Contato)
  if (icon.includes('telephone') || icon.includes('phone') || icon.includes('contact') || icon.includes('contato')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
        />
      </svg>
    );
  }

  // Upload icon (Importações)
  if (icon.includes('upload') || icon.includes('import')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
        />
      </svg>
    );
  }

  // Speedometer/KPI icon
  if (icon.includes('speedometer') || icon.includes('kpi') || icon.includes('gauge')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    );
  }

  // Gift/Raffle icon (Sorteios)
  if (icon.includes('gift') || icon.includes('raffle') || icon.includes('sorteio')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
        />
      </svg>
    );
  }

  // Target/Bullseye icon (Merchandising)
  if (icon.includes('merchandising') || icon.includes('bullseye') || icon.includes('target') || icon.includes('shop-window')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <circle cx="12" cy="12" r="10" strokeWidth={2} />
        <circle cx="12" cy="12" r="6" strokeWidth={2} />
        <circle cx="12" cy="12" r="2" strokeWidth={2} fill="currentColor" />
      </svg>
    );
  }

  // Shop/Store icon (Loja)
  if (icon.includes('shop') || icon.includes('store') || icon.includes('loja')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
        />
      </svg>
    );
  }

  // Wallet/Budget icon (Orçamento)
  if (icon.includes('wallet') || icon.includes('budget') || icon.includes('orcamento') || icon.includes('money')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
        />
      </svg>
    );
  }

  // Question/Quiz icon
  if (icon.includes('question') || icon.includes('quiz') || icon.includes('help')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }

  // Trophy/Ranking icon
  if (icon.includes('trophy') || icon.includes('ranking') || icon.includes('medal')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
        />
      </svg>
    );
  }

  // Map/Route icon (Rotas)
  if (icon.includes('geo') || icon.includes('map') || icon.includes('route') || icon.includes('rota') || icon.includes('location')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    );
  }

  // Download/Export icon
  if (icon.includes('download') || icon.includes('export')) {
    return (
      <svg
        className={clsx(sizeMap[size], className)}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
    );
  }

  // Default circle icon
  return (
    <svg
      className={clsx(sizeMap[size], className)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="3" strokeWidth={2} />
    </svg>
  );
}

export default MenuIcon;
